import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TruncatedText } from '../../../../../../CommonComponents/OPL/Text/TruncatedText';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { AlignmentDTO, OverviewDTO } from '../../../../../api/useOverviewQuery';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { AlignmentCard, AlignmentCardButton } from './Alignment.styles';
import Caret from '../../../../img/caret.svg';
import { useClientId } from '../../../../../../_hooks/useClientId';
import { Avatar } from '../../../../../../CommonComponents/OPL/Avatar/Avatar';
import { getGradientColoursByScore } from '../../../../../utils/getGradientColoursByScore';
import { Button } from '../../../../../../CommonComponents/OPL/Button/Button';
import { RemoveConfirmationModal } from '../RemoveConfirmationModal/RemoveConfirmationModal';

const BAR_WIDTH = 132;

interface Props {
  alignment: AlignmentDTO;
  data: OverviewDTO;
  showRemove: boolean;
}

const AnimatedText = motion(Text);

export const Alignment = ({ alignment, data, showRemove }: Props) => {
  const [selectedAlignment, setSelectedAlignment] = useState<AlignmentDTO | null>(null);
  const clientId = useClientId();

  const colours = getGradientColoursByScore(alignment.score);

  return (
    <>
      <AlignmentCard as="article" css={showRemove ? 'padding: 16px 12px' : 'padding: 0'}>
        <AlignmentCardButton
          css={showRemove ? 'padding: 0 0 4px 0' : 'padding: 16px 12px; height: 100%'}
          onClick={() => {
            ADV.util.Router.go2('BeFi20Results', {
              clientId,
              compareToId: alignment.id,
              isInitiallyComparing: true,
            });
          }}
        >
          <TruncatedText fontWeight={600}>{`With ${alignment.name}:`}</TruncatedText>

          <AnimatedText
            fontWeight={600}
            fontSize="h5"
            initial={{ marginBottom: 40 }}
            animate={{ marginBottom: showRemove ? 24 : 40 }}
          >
            {alignment.similarity} Similarity
          </AnimatedText>

          <Box display="flex" justifyContent="center">
            <Avatar
              isAlt
              size="small"
              style={{
                background: `linear-gradient(180deg, ${colours.base} 0%, ${colours.highlight} 100%)`,
              }}
            >
              {data.persona.initials}
            </Avatar>
            <Avatar
              isAlt
              size="small"
              style={{
                background: `linear-gradient(180deg, ${colours.base} 0%, ${colours.highlight} 100%)`,
                marginLeft: -10,
              }}
            >
              {alignment.initials}
            </Avatar>
          </Box>

          <Box
            pl={(alignment.score / 100) * BAR_WIDTH}
            width={BAR_WIDTH}
            mx="auto"
            mt={6}
            css="text-align: left"
          >
            <Caret aria-hidden style={{ transform: 'translate(-50%)' }} />
          </Box>

          <Box bg={colours.base} width={BAR_WIDTH} height={4} mx="auto" css="border-radius: 1px" />
        </AlignmentCardButton>

        {showRemove && (
          <Button
            variant="subtleInactive"
            size="small"
            mt="8"
            css="width: 100%"
            onClick={() => {
              setSelectedAlignment(alignment);
            }}
          >
            Remove
          </Button>
        )}
      </AlignmentCard>

      {selectedAlignment && (
        <RemoveConfirmationModal
          onClose={() => setSelectedAlignment(null)}
          alignment={selectedAlignment}
        />
      )}
    </>
  );
};
